import {PapayaColorTable, RGBAColor, RGBColor} from '../../dashboard-papaya/symbols/dashboard-papaya-color-table.symbols';
import {
  PapayaScanController,
  PapayaSurfaceController,
} from '../services/dashboard-study-papaya-controller/dashboard-study-papaya-controller.service';
import {PapayaRGBColor} from '../../dashboard-papaya/symbols/dashboard-papaya-general.symbols';
import {DashboardPapayaImageFileMetadata} from './dashboard-study-papaya.types';

/**
 * Describes property name for storing controller in the Papaya viewer.
 * As objects in JavaScript are assigned by reference, we can store the controller in the papaya object without duplicating data.
 * Used for a direct link between the papaya viewer and the controller.
 */
export const propertyNameForStoringControllerInPapaya = '__controller';

/**
 * Main papaya object.
 * Added by the custom Papaya library.
 * Supposed to be loaded dynamically using `rt-dynamic-scripts` service.
 */
export interface OriginalPapaya {
  /**
   * Papaya parameters for the original papaya instance.
   * It controls papaya's behaviour and appearance.
   */
  params: OriginalPapayaParams;

  /**
   * The main papaya container object.
   */
  Container: {
    /** If auto scroll is enabled, the window will be scrolled to (0,0) when papaya is started. */
    disableAutoScroll: boolean;

    /** Initiates the papaya instance. */
    startPapaya: () => void;

    /** Resizes the papaya viewer to fit its parent container. */
    resizePapaya: (ev: Event, force: boolean) => void;
  };
}

/**
 * Papaya instance describes a single papaya viewer (one HTML element that may contain drawn 3D surface and/or slices).
 */
export interface OriginalPapayaInstance {
  /**
   * Sets the loading complete callback.
   * It will be called when the nifti image is loaded.
   */
  setLoadingCompleteCallback: (callback: () => void) => void;

  /**
   * Updates the papaya parameters with the provided values.
   * @param params - new parameters to be set.
   */
  updateContainerParams: (params: Partial<OriginalPapayaParams>) => void;

  /** Controls the papaya view. */
  viewer: OriginalPapayaViewer;
}

export type OriginalPapayaParams = OriginalPapayaBaseParams & (OriginalPapayaDynamicImageParams | OriginalPapayaDynamicSurfaceParams);

/**
 * Papaya parameters for the original papaya instance.
 * It controls papaya's behaviour and appearance.
 */
export interface OriginalPapayaBaseParams {
  /**
   * List of blob image nifti files.
   */
  files?: Blob[];

  /**
   * List of blob surface gifti files.
   */
  binarySurfaces: Blob[];

  /**
   * The function that will be called when any file is loaded.
   */
  loadingComplete: () => void;

  /**
   * Enables the scroll event bubbling.
   */
  allowScroll: boolean;

  /**
   * Enables automatic resizing of the papaya viewer to its parent container.
   */
  scaleToFit: boolean;

  /**
   * Enables the tall orthogonal view layout, stacking sagittal and coronal views vertically.
   */
  orthogonalTall: boolean;

  /**
   * Automatically updates the orthogonal layout when the viewer container is resized.
   */
  enableUpdateOrthogonalOnResize: boolean;

  /**
   * Enables orthogonal view (sagittal, coronal, axial) rendering.
   */
  orthogonal: boolean;

  /**
   * Whether the papaya viewer should be displayed in full-screen mode when only a single viewer is displayed.
   */
  singleSliceFullWidth: boolean;

  /**
   * Enables real-time updates of orthogonal views when interacting with the main view.
   */
  orthogonalDynamic: boolean;

  /**
   * Enables kiosk mode by hiding controls and simplifying UI for display-only environments.
   */
  kioskMode: boolean;

  /**
   * Adds padding around the viewer when in full-screen mode to preserve layout spacing.
   */
  fullScreenPadding: boolean;

  /**
   * Displays the overlay or header showing image metadata, cursor coordinates, etc.
   */
  showDisplay: boolean;

  /**
   * Positions the main image viewer to the right of the orthogonal views.
   */
  putMainImageOnTheRight: boolean;

  /**
   * Calls the provided function when the user starts or stops dragging the papaya viewer.
   * @param isDragging - Indicates whether the user is currently dragging the viewer.
   */
  dragEventEmitter: (isDragging: boolean) => void;

  /**
   * Calls the provided function when the user unpresses the mouse button in the papaya viewer.
   */
  customMouseUpEvent: (viewer: OriginalPapayaViewer) => void;

  /**
   * Calls the provided function when the user presses the mouse button in the papaya viewer.
   */
  customMouseDownEvent: (viewer: OriginalPapayaViewer) => void;

  /**
   * Sets the background color used behind surface renderings (typically 3D overlays).
   */
  surfaceBackground: RGBAColor;

  /**
   * Displays the current voxel value (e.g., intensity) at the cursor position.
   */
  showCurrentValue: boolean;

  /**
   * Shows cursor coordinates in voxel and/or world space.
   */
  showCoordinates: boolean;

  /**
   * Displays UI controls such as sliders, buttons, and dropdowns.
   */
  showControls: boolean;

  /**
   * Renders anatomical planes (sagittal, coronal, axial) in the surface view.
   */
  showSurfacePlanes: boolean;

  /**
   * Shows crosshairs in the 3D surface view aligned with the current slice position.
   */
  show3DCrosshairs: boolean;

  /**
   * Ratio used to adjust zoom level in the 3D surface rendering.
   */
  surfaceZoomRatio: number;

  /**
   * If true, ignores canvas offset when rendering inscriptions (like labels or overlays).
   */
  ignoreCanvasOffsetForInscriptions: boolean;
}

/**
 * Image params are stored by their file names.
 * Moved to a separate interface to avoid conflict with predefined papaya parameters.
 */
export interface OriginalPapayaDynamicImageParams {
  /**
   * Papaya image files are stored just by their file names.
   */
  [imageFileName: string]: OriginalPapayaImageParams;
}

/**
 * Surface params are stored by their file names.
 * Moved to a separate interface to avoid conflict with predefined papaya parameters.
 */
export interface OriginalPapayaDynamicSurfaceParams {
  /**
   * Papaya surface files are stored just by their file names.
   */
  [surfaceFileName: string]: OriginalPapayaSurfaceParams;
}

/**
 * Papaya screen volume.
 * Stores data about a specific slice of the papaya viewer.
 */
export interface OriginalPapayaScreenVolume {
  /**
   * Opacity of the slice; accepts values from 0 to 1.
   */
  alpha: number;

  /**
   * If not empty, the volume will be filtered by the provided values.
   * The only voxels with the values in the array will be shown.
   */
  screenFilterValues?: number[];

  /**
   * The lowest screen label value to be displayed in the slice.
   */
  screenMin: number;

  /**
   * The highest screen label value to be displayed in the slice.
   */
  screenMax: number;

  /**
   * The lowest image label value to be displayed in the slice.
   */
  imageMin: number;

  /**
   * The highest image label value to be displayed in the slice.
   */
  imageMax: number;

  /**
   * The lowest screen label value found in the slice.
   */
  originalScreenMin: number;

  /**
   * The highest screen label value found in the slice.
   */
  originalScreenMax: number;

  /**
   * Color table for the slice.
   * It is used for coloring the slice.
   */
  colorTable: PapayaColorTable;

  /**
   * Custom slice controller.
   */
  [propertyNameForStoringControllerInPapaya]: PapayaScanController;
}

/**
 * Papaya surface.
 * Stores data about a specific surface of the papaya viewer.
 */
export interface OriginalPapayaSurfaces {
  /**
   * Surface opacity; accepts values from 0 to 1.
   */
  alpha: number;

  /**
   * Solid color for the whole surface.
   */
  solidColor: PapayaRGBColor;

  /**
   * Used for selection which part of the surface should be highlighted.
   * If the filter color matches the color in the surface data, the matched part will be colored with the mapped color.
   */
  filterColor: PapayaRGBColor;

  /**
   * Used for coloring the part selected with the filter color.
   */
  mappedColor: PapayaRGBColor;

  /**
   * Indicates whether the surface is hidden.
   */
  hidden: boolean;

  /**
   * Custom surface controller.
   */
  [propertyNameForStoringControllerInPapaya]: PapayaSurfaceController;
}

/**
 * Describes the papaya image file.
 */
export interface OriginalPapayaImageParams {
  /** Opacity of the image. */
  alpha?: number;

  /** Color table of the image. */
  lut: PapayaColorTable;

  /** Minimal label value for the image. */
  min?: number;

  /** Maximal label value for the image. */
  max?: number;

  /** Indicates which labels has to be shown. */
  filterValues?: number[];

  /** Stores our custom metadata to keep the reference to our model. */
  metadata: DashboardPapayaImageFileMetadata;
}

/**
 * Describes the papaya surface file.
 */
export interface OriginalPapayaSurfaceParams {
  /** Color of the surface. */
  color?: RGBColor;

  /** Opacity of the surface. */
  alpha?: number;
}

/**
 * Scan view data and controls of the papaya viewer.
 */
export interface OriginalPapayaScanView {
  /** Current slice index in the papaya viewer. */
  sliceDirection: OriginalPapayaViewerSlice;

  /** Size of the canvas in the papaya viewer. */
  screenDim: number;

  /**
   * The main canvas element of the papaya viewer that stores the rendered image.
   */
  canvasMain: HTMLCanvasElement;

  /**
   * 3x3 matrix that transforms the canvas image according to zoom and size.
   */
  finalTransform: number[];
}

/**
 * Coordinates of the papaya viewer scan view viewer point.
 */
export interface OriginalPapayaViewerScanCoord {
  /** X coordinate in the world space. */
  x: number;

  /** Y coordinate in the world space. */
  y: number;

  /** Z coordinate in the world space. */
  z: number;
}

/**
 * Surface view data and controls.
 */
export interface OriginalPapayaSurfaceView {
  /** Current slice index in the papaya viewer. */
  sliceDirection: OriginalPapayaViewerSlice;

  /**
   * Set the world center to the new point and update the perspective matrix.
   * This function doesn't update the view, it just sets the center point, so you may want to call drawView() after this.
   * It is also necessary to reset the viewer to apply the following transformations correctly.
   *
   * So, to change the world center correctly, you'll probably want:
   * 1. Reset the viewer calling functions like resetViewer, resetOrientation, etc.
   * 2. Call this function to set the new center point.
   * 3. Call drawView to update the view.
   *
   * Step 1 and 2 can be swapped if necessary.
   *
   * @param customPoint {[number, number, number]} - The new center point in the world coordinates.
   */
  movePerspectiveToPoint: (customPoint: [number, number, number]) => void;

  /**
   * Current scan view viewer point coordinates.
   */
  currentCoord: OriginalPapayaViewerScanCoord;

  /**
   * Updates the sizes of the papaya surface view from the volume.
   */
  updateSizes: () => void;

  /**
   * Resets the papaya surface view to the default orientation.
   */
  resetOrientation: () => void;

  /**
   * Resets the papaya surface view to the directly facing the body.
   */
  resetOrientationForBody: () => void;

  /** Controls the papaya dimensions and resizing. */
  volume: OriginalPapayaVolume;

  /** The 3D perspective zoom level for the surface view. */
  zoom: number;
}

/**
 * Controls the papaya viewer.
 */
export interface OriginalPapayaViewer {
  /**
   * Draws the papaya viewer.
   * @param force - indicates whether all images should be recalculated and redrawn.
   * @param skipUpdate - indicates whether the images should be only repainted.
   */
  drawViewer: (force: boolean, skipUpdate: boolean) => void;

  /**
   *
   * @param slice
   * @param canvas
   */
  drawScreenSliceOnCustomCanvas: (slice: OriginalPapayaScanView, canvas: HTMLCanvasElement) => void;

  /**
   * Loads the image to the papaya viewer.
   * @param image - image to be loaded.
   */
  loadImage: (image: Blob[]) => void;

  /**
   * Remove slice from the papaya viewer.
   * @param sliceIndex - index of the slice to be removed.
   * @param redraw - whether to redraw to single surface view when the last screen slice is removed.
   */
  closeOverlay: (sliceIndex: number, redraw: boolean) => void;

  /** Link to the papaya container instance. */
  container: OriginalPapayaContainerInstance;

  /**
   * Loads the surface to the papaya viewer.
   * @param surface - surface to be loaded.
   */
  loadSurface: (surface: Blob[]) => void;

  /**
   * Load multiple surfaces at once asynchronously.
   */
  bunchSurfaceLoad: (controls: PapayaSurfaceController[]) => void;

  /**
   * Remove surface from the papaya viewer.
   * @param surfaceIndex - index of the surface to be removed.
   */
  removeSurface: (surfaceIndex: number, redrawViewer: boolean) => void;

  /**
   * Reorders the views in the papaya viewer.
   * @param sliceEnum - slice to be reordered.
   * @param redrawAfterReorder - indicates whether to redraw the viewer after reordering.
   */
  reorderViewsToSpecificPosition: (sliceEnum: OriginalPapayaViewerSlice, redrawAfterReorder: boolean) => void;

  /**
   * Reorders the views in the papaya viewer.
   * Skips the checks for the slice presence; can be useful if called in the middle of transformation without redrawing.
   * @param sliceEnum - slice to be reordered.
   * @param redrawAfterReorder - indicates whether to redraw the viewer after reordering.
   */
  reorderViewsToSpecificPositionUnsafe: (sliceEnum: OriginalPapayaViewerSlice, redrawAfterReorder: boolean) => void;

  /**
   * Stores papaya screen slice volumes.
   */
  screenVolumes: OriginalPapayaScreenVolume[];

  /**
   * Sets the zoom factor for the papaya viewer.
   */
  setZoomFactor: (zoomFactor: number, skipUpdate?: boolean) => void;

  /**
   * Set a coordinate in the viewer to be the point of the zoom.
   * Papaya has a bit specific way of applying zoom.
   * The farthest point from the center of 3D space, the highest the value should be.
   * Formula: panAmount = currentShift + viewerSize * (currentShift / maximalPossibleShift).
   * Where:
   * `currentShift` is the distance from the center of 3D space to the point of zoom.
   * `viewerSize` is the size of the square in the current zoom level.
   * `maximalPossibleShift` is the distance from the center of 3D space to the farthest point (255) minus `viewerSize`.
   *
   * @param horizontalLoc The point needed to be achieved in the horizontal direction (x, y, z can be different depending on the slice direction).
   * @param verticalLoc The point needed to be achieved in the vertical direction (x, y, z can be different depending on the slice direction).
   * @param deepLock The point needed to be achieved in the deep direction (x, y, z can be different depending on the slice direction).
   * @param sliceDirection In which slice direction the zoom should be applied.
   */
  setExactPanLocation: (horizontalLoc: number, verticalLoc: number, deepLock: number, sliceDirection: OriginalPapayaViewerSlice) => void;

  /**
   * Stores papaya rendered surfaces.
   */
  surfaces: OriginalPapayaSurfaces[];

  /**
   * Stores papaya surface view.
   * Present only if at least one surface is loaded.
   */
  surfaceView?: OriginalPapayaSurfaceView;

  /**
   * Indicates whether the current view should include a surface view.
   */
  isSurfaceViewMode: boolean;

  /**
   * Currently displayed slice on the main image position.
   */
  mainImage: OriginalPapayaScanView | OriginalPapayaSurfaceView;

  /**
   * Currently displayed slice on the upper image position.
   */
  lowerImageTop: OriginalPapayaScanView | OriginalPapayaSurfaceView;

  /**
   * Currently displayed slice on the lower image position.
   */
  lowerImageBot: OriginalPapayaScanView | OriginalPapayaSurfaceView;

  /**
   * Currently displayed slice on the upper image position for the second lower image.
   */
  lowerImageBot2: OriginalPapayaScanView | OriginalPapayaSurfaceView;

  /**
   * Current axial slice.
   */
  axialSlice: OriginalPapayaScanView;

  /**
   * Current coronal slice.
   */
  coronalSlice: OriginalPapayaScanView;

  /**
   * Current sagittal slice.
   */
  sagittalSlice: OriginalPapayaScanView;

  /**
   * Current scan view viewer point coordinates.
   */
  currentCoord: OriginalPapayaViewerScanCoord;

  /** Controls the papaya dimensions and resizing. */
  volume: OriginalPapayaVolume;
}

/**
 * Papaya volume instance.
 * It is used to control the papaya viewer volume.
 */
export interface OriginalPapayaVolume {
  /** The world coordinates for the papaya viewer. */
  header: OriginalPapayaVolumeHeader;

  /** The papaya viewer scan X dimensions. */
  getXDim(): number;

  /** The papaya viewer scan Y dimensions. */
  getYDim(): number;

  /** The papaya viewer scan Z dimensions. */
  getZDim(): number;
}

/**
 * Papaya volume header.
 */
export interface OriginalPapayaVolumeHeader {
  imageDimensions: OriginalPapayaVolumeHeaderImageDimensions;

  voxelDimensions: OriginalPapayaVolumeHeaderVoxelDimensions;

  origin: OriginalPapayaViewerScanCoord;
}

/**
 * Dimensions of the papaya volume header image.
 */
export interface OriginalPapayaVolumeHeaderImageDimensions {
  xDim: number;
  yDim: number;
  zDim: number;
}

/**
 * Voxel dimensions of the papaya volume header.
 */
export interface OriginalPapayaVolumeHeaderVoxelDimensions {
  xSize: number;
  ySize: number;
  zSize: number;
}

/**
 * Papaya container instance.
 */
export interface OriginalPapayaContainerInstance {
  /** Recalculates the world coordinates for the papaya viewer. */
  coordinateChanged: (viewer: OriginalPapayaViewer) => void;

  /** Recalculates size of the papaya components. */
  resizeViewerComponents: (resize: boolean) => void;

  /** Enables orthogonal view (sagittal, coronal, axial) rendering. */
  orthogonal: OriginalPapayaParams['orthogonal'];

  /** Whether the papaya viewer should be displayed in full-screen mode when only a single viewer is displayed. */
  singleSliceFullWidth: OriginalPapayaParams['singleSliceFullWidth'];
}

/**
 * Papaya viewer slice.
 */
export enum OriginalPapayaViewerSlice {
  Axial = 1,
  Coronal = 2,
  Sagittal = 3,
  Surface = 5,
}
